/** Pure trail geometry: measure hubs → rim-to-rim cubic dashed path. */

export type TrailNode = { x: number; y: number; r: number };

export function rimPoint(from: TrailNode, to: { x: number; y: number }): { x: number; y: number } {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy) || 1;
  return { x: from.x + (dx / len) * from.r, y: from.y + (dy / len) * from.r };
}

/** Build an SVG path of cubic segments joining consecutive circle rims. */
export function buildCurvePath(nodes: TrailNode[]): string {
  if (nodes.length < 2) return '';
  const parts: string[] = [];
  for (let i = 0; i < nodes.length - 1; i += 1) {
    const a = nodes[i]!;
    const b = nodes[i + 1]!;
    const p0 = rimPoint(a, b);
    const p3 = rimPoint(b, a);
    const dx = p3.x - p0.x;
    const dy = p3.y - p0.y;
    const pull = Math.abs(dx) * 0.5;
    const sign = dx === 0 ? 1 : Math.sign(dx);
    const p1 = { x: p0.x + sign * pull, y: p0.y + dy * 0.05 };
    const p2 = { x: p3.x - sign * pull, y: p3.y - dy * 0.05 };
    if (i === 0) parts.push(`M ${p0.x.toFixed(2)} ${p0.y.toFixed(2)}`);
    parts.push(
      `C ${p1.x.toFixed(2)} ${p1.y.toFixed(2)} ${p2.x.toFixed(2)} ${p2.y.toFixed(2)} ${p3.x.toFixed(2)} ${p3.y.toFixed(2)}`,
    );
  }
  return parts.join(' ');
}

/** Count cubic segments in a path `d` (M + N×C → N). */
export function cubicSegmentCount(pathD: string): number {
  const matches = pathD.match(/\bC\b/g);
  return matches ? matches.length : 0;
}

/** Measure `.hub` circles in DOM order relative to the snake container. */
export function measureTrailNodes(snake: HTMLElement): TrailNode[] {
  const snakeBox = snake.getBoundingClientRect();
  const hubs = snake.querySelectorAll<HTMLElement>('.hub');
  const nodes: TrailNode[] = [];
  hubs.forEach((el) => {
    const box = el.getBoundingClientRect();
    nodes.push({
      x: box.left + box.width / 2 - snakeBox.left,
      y: box.top + box.height / 2 - snakeBox.top,
      r: Math.min(box.width, box.height) / 2,
    });
  });
  return nodes;
}

export function syncSnakePath(snake: HTMLElement, pathEl: SVGPathElement): void {
  pathEl.setAttribute('d', buildCurvePath(measureTrailNodes(snake)));
}
